import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { earliestDate, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Checkbox, Col, DatePicker, Form, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Rule } from "antd/lib/form";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import LabelWithPopover from "../../../../../../common/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "../../../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../../../common/constants";
import { ContractStatus, InsurancePeriod, PaymentFrequency, PeriodType } from "../../../../enums";
import { CreateUpdateContractGainerRecord, CreateUpdateInsuranceContract } from "../../../../types";
import {
  calculateContractStatus,
  calculateGainerRecordStartDates,
  calculateInsurancePeriodEndDate,
  calculateLastContractCancellationDate,
} from "../../../../utils";
import ContractStatusTag from "../../../tags/ContractStatusTag";

const colSpan = 4;

interface Props {
  insurancesCount: number;
  insurancesAnnualPremiumsSum: number;
  insurancesPartialPremiumsSum: number;
}

const InsuranceContractFormDataSection = ({ insurancesCount, ...props }: Props) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CreateUpdateInsuranceContract>();

  const handlePeriodTypeChange = (periodType: PeriodType) => {
    if (periodType === PeriodType.INDEFINITE) {
      form.setFieldsValue({
        insurancePeriod: InsurancePeriod.TECHNICAL_YEAR,
        effectiveEndDate: undefined,
      });
    } else {
      form.setFieldsValue({ insurancePeriod: InsurancePeriod.FIXED });
    }
    handleStatusDefiningDateChange();
  };

  const handleInsurancePeriodChange = () => {
    setContractSpecificDates(getContractStatus());
  };

  const handleStatusDefiningDateChange = () => {
    const status = getContractStatus();
    form.setFieldsValue({ status });
    setContractSpecificDates(status);
  };

  const handleEffectiveBeginningChange = () => {
    handleStatusDefiningDateChange();
    const effectiveBeginningDate = form.getFieldValue(["effectiveBeginningDate"]) as string;
    const records = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];
    form.setFieldsValue({ gainerRecords: calculateGainerRecordStartDates(records, effectiveBeginningDate) });
  };

  const handleTransferredToOtherBrokerChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({ transferredToOtherBrokerDate: undefined });
      handleStatusDefiningDateChange();
    }
  };

  const getContractStatus = () => {
    const { effectiveBeginningDate, effectiveEndDate, cancellationDate, transferredToOtherBrokerDate } =
      form.getFieldsValue([
        ["effectiveBeginningDate"],
        ["effectiveEndDate"],
        ["cancellationDate"],
        ["transferredToOtherBrokerDate"],
      ]) as CreateUpdateInsuranceContract;

    return calculateContractStatus({
      startDate: effectiveBeginningDate,
      endDate: effectiveEndDate,
      cancellationDate: cancellationDate,
      transferredToOtherBrokerDate: transferredToOtherBrokerDate,
    });
  };

  const setContractSpecificDates = (status?: ContractStatus) => {
    const {
      cancellationDate,
      periodType,
      effectiveEndDate,
      insurancePeriod,
      effectiveBeginningDate,
      transferredToOtherBrokerDate,
    } = form.getFieldsValue([
      ["cancellationDate"],
      ["periodType"],
      ["effectiveEndDate"],
      ["insurancePeriod"],
      ["effectiveBeginningDate"],
      ["transferredToOtherBrokerDate"],
    ]) as CreateUpdateInsuranceContract;

    const insurancePeriodEndDate = calculateInsurancePeriodEndDate(
      cancellationDate,
      periodType,
      effectiveEndDate,
      insurancePeriod,
      effectiveBeginningDate,
      transferredToOtherBrokerDate,
      status,
    );
    const lastContractCancellationDate = calculateLastContractCancellationDate(
      periodType,
      cancellationDate,
      transferredToOtherBrokerDate,
      effectiveBeginningDate,
      insurancePeriodEndDate,
    );

    form.setFieldsValue({ insurancePeriodEndDate, lastContractCancellationDate });
  };

  const setPremiumsSumToForm = (type: "annualPremium" | "partialPremium") => {
    if (type === "annualPremium") {
      form.setFieldsValue({ annualPremium: props.insurancesAnnualPremiumsSum });
    } else {
      form.setFieldsValue({ partialPremium: props.insurancesPartialPremiumsSum });
    }
  };

  const resolvePremiumLabel = (type: "annualPremium" | "partialPremium") => {
    const label = type === "annualPremium" ? t("contract.attrs.annualPremium") : t("contract.attrs.partialPremium");

    if (insurancesCount > 1) {
      const currentValue = form.getFieldValue([type === "annualPremium" ? "annualPremium" : "partialPremium"]);
      const insurancesSumValue =
        type === "annualPremium" ? props.insurancesAnnualPremiumsSum : props.insurancesPartialPremiumsSum;
      const helperLabel = t(
        "contract.helpers." +
          (type === "annualPremium" ? "insurancesAnnualPremiumsSum" : "insurancesPartialPremiumsSum"),
        { premiumsSum: formatLocaleCurrency(insurancesSumValue) },
      );

      return currentValue !== insurancesSumValue ? (
        <LabelWithPopover
          label={label}
          popoverContent={
            <>
              <div>{helperLabel}</div>
              <div className="right-align">
                <Button style={{ paddingRight: "0" }} type="link" onClick={() => setPremiumsSumToForm(type)}>
                  {t("contract.actions.putPremiumsSumToContract")}
                </Button>
              </div>
            </>
          }
        />
      ) : (
        label
      );
    }

    return label;
  };

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.contractData")}
      extra={
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.status !== next.status}>
          {({ getFieldValue }) => {
            const status = getFieldValue("status");
            return status ? <ContractStatusTag status={status} /> : undefined;
          }}
        </Form.Item>
      }
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.signDate !== next.signDate}>
            {({ getFieldValue }) => {
              const signDate = getFieldValue("signDate") as string | undefined;
              const rules: Rule[] = [validations.notNull];
              if (signDate) {
                rules.push(validations.notAfter(signDate, t("contract.attrs.signDateInsurance")));
              }
              return (
                <Form.Item
                  name="mediationReportSignDate"
                  label={t("contract.attrs.mediationReportSignDate")}
                  rules={rules}
                  {...datePickerFormItemProps}
                >
                  <DatePicker {...datePickerStandardProps} maxDate={toDate(signDate)} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.effectiveBeginningDate !== next.effectiveBeginningDate}>
            {({ getFieldValue }) => {
              const effectiveBeginningDate = getFieldValue("effectiveBeginningDate");
              const rules: Rule[] = [validations.notNull];
              if (effectiveBeginningDate) {
                rules.push(validations.notAfter(effectiveBeginningDate, t("contract.attrs.effectiveBeginningDate")));
              }
              return (
                <Form.Item
                  name="signDate"
                  label={t("contract.attrs.signDateInsurance")}
                  rules={rules}
                  {...datePickerFormItemProps}
                >
                  <DatePicker {...datePickerStandardProps} maxDate={toDate(effectiveBeginningDate)} />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="effectiveBeginningDate"
            label={t("contract.attrs.effectiveBeginningDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} onChange={handleEffectiveBeginningChange} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item name="periodType" label={t("contract.enums.periodType._label")} rules={[validations.notNull]}>
            <Select
              {...selectStandardProps}
              options={Object.keys(PeriodType).map((type) => ({
                value: type,
                label: t("contract.enums.periodType." + type),
              }))}
              onChange={handlePeriodTypeChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.effectiveBeginningDate !== next.effectiveBeginningDate || prev.periodType !== next.periodType
            }
          >
            {({ getFieldValue }) => {
              const beginningDate = getFieldValue("effectiveBeginningDate") as string | undefined;
              const periodType = getFieldValue("periodType") as PeriodType;
              const rules: Rule[] = periodType === PeriodType.DEFINITE ? [validations.notNull] : [validations.none];
              if (beginningDate) {
                rules.push(validations.notBefore(beginningDate, t("contract.attrs.effectiveBeginningDate")));
              }
              return (
                <Form.Item
                  name="effectiveEndDate"
                  label={t("contract.attrs.effectiveEndDate")}
                  rules={rules}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerStandardProps}
                    disabled={periodType === PeriodType.INDEFINITE}
                    minDate={toDate(beginningDate)}
                    onChange={handleStatusDefiningDateChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.effectiveBeginningDate !== next.effectiveBeginningDate ||
              prev.effectiveEndDate !== next.effectiveEndDate
            }
          >
            {({ getFieldValue }) => {
              const beginningDate = getFieldValue("effectiveBeginningDate") as string | undefined;
              const endDate = getFieldValue("effectiveEndDate") as string | undefined;
              const rules = [];
              if (beginningDate) {
                rules.push(validations.notBefore(beginningDate, t("contract.attrs.effectiveBeginningDate")));
              }
              if (endDate) {
                rules.push(validations.notSameOrAfter(endDate, t("contract.attrs.effectiveEndDate")));
              }
              return (
                <Form.Item
                  name="cancellationDate"
                  label={t("contract.attrs.cancellationDate")}
                  rules={rules.length > 0 ? rules : [validations.none]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerClearableProps}
                    minDate={toDate(beginningDate)}
                    maxDate={toDate(endDate)?.subtract(1, "day")}
                    onChange={handleStatusDefiningDateChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="paymentFrequency"
            label={t("contract.enums.paymentFrequency._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(PaymentFrequency).map((frequency) => ({
                value: frequency,
                label: t("contract.enums.paymentFrequency." + frequency),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="annualPremium"
            label={resolvePremiumLabel("annualPremium")}
            rules={[validations.notNull, validations.minNumber(0.01)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.annualPremium !== next.annualPremium}>
            {({ getFieldValue }) => (
              <Form.Item
                name="partialPremium"
                label={resolvePremiumLabel("partialPremium")}
                rules={[
                  validations.notNull,
                  validations.minNumber(0.01),
                  validations.maxNumber(getFieldValue("annualPremium"), t("contract.attrs.annualPremium")),
                ]}
              >
                <InputNumber type="decimal" addon="euro" />
              </Form.Item>
            )}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.periodType !== next.periodType}>
            {({ getFieldValue }) => {
              const periodType = getFieldValue("periodType") as PeriodType;
              const options = [];
              if (periodType === PeriodType.INDEFINITE) {
                options.push(InsurancePeriod.TECHNICAL_YEAR, InsurancePeriod.CALENDAR_YEAR);
              } else if (periodType === PeriodType.DEFINITE) {
                options.push(InsurancePeriod.FIXED);
              }
              return (
                <Form.Item
                  name="insurancePeriod"
                  label={t("contract.enums.insurancePeriod._label")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={options.map((period) => ({
                      value: period,
                      label: t("contract.enums.insurancePeriod." + period),
                    }))}
                    onChange={handleInsurancePeriodChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="insurancePeriodEndDate"
            label={t("contract.attrs.insurancePeriodEndDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} disabled />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="lastContractCancellationDate"
            label={t("contract.attrs.lastContractCancellationDate")}
            rules={[validations.none]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} disabled />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="transferredFromOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredFromOtherBroker")}
                tooltip={t("contract.helpers.transferredFromOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }} onChange={handleTransferredToOtherBrokerChange}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBroker")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.effectiveBeginningDate !== next.effectiveBeginningDate ||
              prev.effectiveEndDate !== next.effectiveEndDate ||
              prev.cancellationDate !== next.cancellationDate ||
              prev.transferredToOtherBroker !== next.transferredToOtherBroker
            }
          >
            {({ getFieldValue }) => {
              const beginningDate = getFieldValue("effectiveBeginningDate") as string | undefined;
              const endDate = getFieldValue("effectiveEndDate") as string | undefined;
              const cancellationDate = getFieldValue("cancellationDate") as string | undefined;
              const transferredToOtherBroker = getFieldValue("transferredToOtherBroker");
              const rules = [];
              if (beginningDate) {
                rules.push(validations.notSameOrBefore(beginningDate, t("contract.attrs.effectiveBeginningDate")));
              }
              if (cancellationDate) {
                rules.push(validations.notAfter(cancellationDate, t("contract.attrs.cancellationDate")));
              } else if (endDate) {
                rules.push(validations.notAfter(endDate, t("contract.attrs.effectiveEndDate")));
              }
              if (transferredToOtherBroker) {
                rules.push(validations.notNull);
              }
              return (
                <Form.Item
                  name="transferredToOtherBrokerDate"
                  label={
                    <LabelWithTooltip
                      label={t("contract.attrs.transferredToOtherBrokerDate")}
                      tooltip={t("contract.helpers.transferredToOtherBrokerDateDesc")}
                    />
                  }
                  rules={rules.length > 0 ? rules : [validations.none]}
                  {...datePickerFormItemProps}
                >
                  <DatePicker
                    {...datePickerClearableProps}
                    disabled={!transferredToOtherBroker}
                    minDate={toDate(beginningDate)?.add(1, "day")}
                    maxDate={earliestDate(cancellationDate, endDate)}
                    onChange={handleStatusDefiningDateChange}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="margin-top-small">
        <Col span={24}>
          <Form.Item
            name="note"
            label={t("contract.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};

export default InsuranceContractFormDataSection;
